import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { mergeMap } from "rxjs/operators";
import { EnvironmentService } from "../environment.service";
import { isSameOriginPublicApi } from "./api-origin.util";

export interface PublicRequestOptions {
  observe?: "body";
  params?: { [param: string]: string | string[] };
  reportProgress?: boolean;
  responseType?: "json";
  withCredentials?: boolean;
}

/**
 * Public API HTTP client.
 *
 * Dual-mode auth (cookie hybrid):
 * - Prefer cookie session (same-origin BFF): send credentials, no bearer from JS.
 * - Fallback: legacy localStorage.api_key + authorization header (quoter / old flows).
 */
@Injectable({
  providedIn: "root",
})
export class PublicHttpClientService {
  constructor(private http: HttpClient, private environmentService: EnvironmentService) {}

  private get endpoint(): string {
    return this.environmentService.get("api.url") || "";
  }

  public post(url: string, body: any, options: any = {}): Observable<any> {
    return this.withAuthOptions(options, true).pipe(
      mergeMap((allOps) => this.http.post(this.endpoint + url, body, allOps))
    );
  }

  public get(url: string, options: any = {}): Observable<any> {
    return this.withAuthOptions(options, false).pipe(mergeMap((allOps) => this.http.get(this.endpoint + url, allOps)));
  }

  public put(url: string, body: any, options: any = {}): Observable<any> {
    return this.withAuthOptions(options, true).pipe(
      mergeMap((allOps) => this.http.put(this.endpoint + url, body, allOps))
    );
  }

  public delete(url: string, options: any = {}): Observable<any> {
    return this.withAuthOptions(options, true).pipe(
      mergeMap((allOps) => this.http.delete(this.endpoint + url, allOps))
    );
  }

  private withAuthOptions(options: any, isMutation: boolean): Observable<any> {
    const apiKey = localStorage.getItem("api_key");
    let headers = options.headers instanceof HttpHeaders ? options.headers : new HttpHeaders(options.headers || {});

    if (apiKey) {
      headers = headers.set("Authorization", `Bearer ${apiKey}`);
    }

    if (isMutation) {
      headers = headers.set("X-Requested-With", "XMLHttpRequest");
    }

    const useCredentials = options.withCredentials ?? isSameOriginPublicApi(this.endpoint);

    return of({
      ...options,
      headers,
      withCredentials: useCredentials,
    });
  }
}
